/**
 * useActionHistory Hook
 * Phase 1 Day 4 - Action history state management
 *
 * Manages device action history with a ref-based cache for the most recent action.
 * This enables efficient access to the latest action without prop drilling while
 * maintaining full history in state.
 *
 * @returns {Object} Action history state and management functions
 */

import { useState, useRef, useEffect } from "react";
import logger from "../utils/logger";

export const useActionHistory = () => {
  // Action history state (array of action objects)
  const [actionHistory, setActionHistory] = useState([]);

  // Ref to hold the latest single action (most recent in history)
  // This provides O(1) access to the most recent action for WebSocket handlers
  const lastActionRef = useRef(null);

  // Sync ref whenever actionHistory changes
  useEffect(() => {
    // Update the ref with the first item in the history (most recent)
    lastActionRef.current = actionHistory.length > 0 ? actionHistory[0] : null;

    if (actionHistory.length > 0) {
      logger.log(
        `[useActionHistory] Updated lastActionRef:`,
        actionHistory[0]?.type || "unknown"
      );
    }
  }, [actionHistory]);

  /**
   * Add an action to the history
   * @param {Object} action - The action object to add
   */
  const addAction = (action) => {
    logger.log(`[useActionHistory] Adding action:`, action);
    setActionHistory((prev) => [action, ...prev]);
  };

  /**
   * Clear all action history
   */
  const clearHistory = () => {
    logger.log("[useActionHistory] Clearing action history");
    setActionHistory([]);
  };

  /**
   * Get the most recent action from the ref (for closures)
   * @returns {Object|null} The most recent action or null
   */
  const getLastAction = () => {
    return lastActionRef.current;
  };

  /**
   * Get action history up to a specified limit
   * @param {number} limit - Maximum number of actions to return
   * @returns {Array} Limited action history
   */
  const getLimitedHistory = (limit) => {
    return actionHistory.slice(0, limit);
  };

  /**
   * Check if there are any actions in history
   * @returns {boolean} True if history has actions
   */
  const hasHistory = () => {
    return actionHistory.length > 0;
  };

  return {
    // State
    actionHistory,
    lastActionRef,

    // Actions
    setActionHistory, // Backward compatibility
    addAction,
    clearHistory,

    // Utilities
    getLastAction,
    getLimitedHistory,
    hasHistory,
  };
};
